import { motion } from "framer-motion";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CookieBannerProps {
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onManagePreferences: () => void;
}

export function CookieBanner({ onAcceptAll, onRejectAll, onManagePreferences }: CookieBannerProps) {
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="fixed bottom-0 left-0 right-0 z-[60]"
      role="region"
      aria-label="Cookie consent"
      aria-live="polite"
    >
      <div className="bg-gradient-to-r from-navy-900 to-navy-800 border-t-2 border-azure-500/30 shadow-2xl">
        <div className="container mx-auto px-4 py-4 sm:py-5">
          {/* Desktop: single row — text left, buttons right */}
          {/* Mobile: stacked — text on top, buttons below */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Text section */}
            <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
              <Cookie className="h-6 w-6 text-azure-400 flex-shrink-0 mt-0.5 sm:mt-0" aria-hidden="true" />
              <div>
                <h2 className="text-base font-semibold text-white sm:hidden">
                  We Value Your Privacy
                </h2>
                <p className="text-sm text-gray-100 leading-relaxed">
                  We use cookies to improve your experience and analyze site traffic.
                  You can accept, reject, or customize your preferences.
                </p>
              </div>
            </div>

            {/* Buttons section */}
            <div className="flex flex-col sm:flex-row gap-3 sm:flex-shrink-0">
              <Button
                onClick={onRejectAll}
                variant="outline"
                className="bg-transparent hover:bg-white/10 text-white hover:text-white border-2 border-white/80 hover:border-white font-semibold rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-white transition-all active:translate-y-[1px] min-h-[48px] sm:min-h-[40px]"
              >
                Reject All
              </Button>
              <Button
                onClick={onManagePreferences}
                variant="outline"
                className="bg-transparent hover:bg-white/10 text-white hover:text-white border-2 border-white/80 hover:border-white font-semibold rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-white transition-all active:translate-y-[1px] min-h-[48px] sm:min-h-[40px]"
              >
                Manage Preferences
              </Button>
              <Button
                onClick={onAcceptAll}
                className="bg-white hover:bg-white/90 text-navy-900 hover:text-navy-800 font-bold rounded-lg shadow-xl hover:shadow-2xl focus:ring-2 focus:ring-offset-2 focus:ring-white transition-all active:translate-y-[1px] min-h-[48px] sm:min-h-[40px]"
              >
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
